/**
 * Aerial perspective — the one effect that sells altitude.
 *
 * CLAUDE.md is blunt about it: distant terrain melting into the atmosphere "sells altitude more
 * than anything else". It is worth being precise about why, because it is easy to dismiss as
 * "just fog". Human depth perception past a few hundred metres is almost entirely *contrast*
 * based — we judge how far a ridge is by how washed out and how blue it looks compared to the
 * ridge in front of it. Render a mountain 60 km away at full contrast and the eye reads the
 * whole scene as a painted backdrop a few hundred metres deep, however many triangles it has.
 * Add the extinction curve and the same geometry suddenly has tens of kilometres of air in it.
 *
 * ## Milestone 3: the same physics as the sky, not a lookalike
 *
 * M2 shipped the *shape* of the curve with hand-picked colours. The problem with that — and it
 * was visible — is that the haze on a distant ridge and the sky just above that ridge were
 * computed by two different models, so they did not match. The eye is extremely good at
 * spotting a horizon where the mountains fade to one colour and the sky is another; it reads as
 * a cut-out.
 *
 * This version uses the *same* scattering coefficients, the same phase functions and the same
 * sun transmittance as `atmosphere/model.ts`, so a ridge at the limit of visibility converges
 * exactly onto the sky behind it. That convergence is the whole effect.
 *
 * ## The integral, and why it is closed-form
 *
 * Along a ray from the eye to a surface, the air thins exponentially with height. Because a
 * straight ray's *height* varies linearly with distance, the optical depth has an exact
 * elementary solution — no raymarch, no lookup:
 *
 *     ∫₀ᵈ exp(−h(s)/H) ds  =  d · exp(−h₀/H) · (1 − e^(−t)) / t,   t = (h₁ − h₀)/H
 *
 * That is six instructions and it is *exact* for the model, which is better than a sampled
 * approximation would be at any step count. The `(1 − e^(−t))/t` factor is removable at `t = 0`
 * (level flight over flat ground — the common case, so it has to be right), which is why it is
 * blended with its Taylor series near zero rather than divided naively.
 *
 * In-scattering then uses the standard analytic result for a medium of constant single-scatter
 * albedo: the light scattered toward the eye along the whole path is the phase-weighted
 * scattering fraction times `(1 − T)`. Rayleigh and Mie are carried separately because their
 * phase functions and scale heights differ — which is exactly why distant terrain goes *blue*
 * while haze near the sun goes *white*.
 *
 * ## Two corrections that are easy to miss
 *
 * **Curvature.** `positionWorld.y` is height above the origin's tangent plane, not altitude. At
 * 100 km that is 780 m of difference, and terrain hangs below the plane by exactly that much, so
 * using it raw makes distant mountains sit in far denser air than they really do. The `d²/2R`
 * drop is added back.
 *
 * **Multiple scattering.** Without it, terrain in shadow at 40 km goes almost black instead of
 * hazy blue-grey. The ambient term comes from the same table the sky uses.
 *
 * `updateFrame(alpha)`: yes — pushes altitude, sun transmittance and the ambient term.
 * Allocation-free.
 */

import { Vector3 } from 'three';
import type { Node } from 'three/webgpu';
import {
  cameraPosition,
  clamp,
  dot,
  exp,
  float,
  max,
  mix,
  normalize,
  output,
  positionWorld,
  smoothstep,
  uniform,
  vec3,
  vec4,
} from 'three/tsl';
import {
  EARTH_ATMOSPHERE,
  miePhase,
  rayleighPhase,
  rgb,
  type MultipleScatteringTable,
  type Rgb,
  type TransmittanceTable,
} from './atmosphere/model.js';

/** Mean Earth radius used for the curvature correction [m]. Matches world/geodesy.ts. */
const EARTH_RADIUS_M = 6_371_008.8;

const scratchSun = /* #__PURE__ */ rgb();
const scratchAmbient = /* #__PURE__ */ rgb();

export interface AerialPerspectiveOptions {
  transmittance: TransmittanceTable;
  multipleScattering: MultipleScatteringTable;
}

export class AerialPerspective {
  /** Assign this to `scene.fogNode`. */
  readonly node;

  private readonly transmittanceTable: TransmittanceTable;
  private readonly multipleScatteringTable: MultipleScatteringTable;

  private readonly sunDirection = uniform(new Vector3(0.42, 0.66, 0.28).normalize());
  /** Sunlight surviving to the camera's altitude, per channel. */
  private readonly sunTransmittance = uniform(new Vector3(1, 1, 1));
  /** Isotropic multiple-scattered radiance at the camera's altitude, per channel. */
  private readonly ambient = uniform(new Vector3(0, 0, 0));
  private readonly cameraAltitude = uniform(0);
  /**
   * Extra Mie density multiplier, 1 = clean air. Weather drives this: an overcast day is hazier
   * near the ground than a clear one, and it is the aerosol term that carries that.
   */
  private readonly haze = uniform(1);

  constructor(options: AerialPerspectiveOptions) {
    this.transmittanceTable = options.transmittance;
    this.multipleScatteringTable = options.multipleScattering;

    const params = EARTH_ATMOSPHERE;

    const toSurface = positionWorld.sub(cameraPosition);
    const distance = toSurface.length();
    const viewDirection = normalize(toSurface);

    // --- true altitudes -----------------------------------------------------------------------
    // Add the curvature drop back: the terrain was built on the ellipsoid and then expressed in
    // the tangent plane, so its `y` is short of its real altitude by d²/2R.
    const horizontal = positionWorld.xz;
    const curvature = dot(horizontal, horizontal).div(2 * EARTH_RADIUS_M);
    const surfaceAltitude = max(positionWorld.y.add(curvature), float(0));
    const cameraAltitude = max(this.cameraAltitude, float(0));

    // --- optical depth, in closed form -----------------------------------------------------
    const columnDensity = (scaleHeight: number) => {
      const t = surfaceAltitude.sub(cameraAltitude).div(scaleHeight);
      const atCamera = exp(cameraAltitude.div(-scaleHeight));
      /*
       * (1 − e^(−t))/t, which is 1 at t = 0 and has a removable singularity there. Level flight
       * over level ground puts `t` at exactly zero every frame, so the naive division would
       * divide by zero in the most ordinary situation there is. Blend to the Taylor series
       * instead — branchless, and correct to a part in a million on both sides of the join.
       */
      const taylor = float(1).sub(t.mul(0.5)).add(t.mul(t).div(6));
      const exact = float(1).sub(exp(t.negate())).div(t.add(1e-5));
      const shape = mix(taylor, exact, smoothstep(0.01, 0.05, t.abs()));
      return distance.mul(atCamera).mul(shape);
    };

    // Metres of sea-level-equivalent air along the ray, per species.
    const rayleighColumn = columnDensity(params.rayleighScaleHeightM);
    const mieColumn = columnDensity(params.mieScaleHeightM).mul(this.haze);

    const betaRayleigh = vec3(
      params.rayleighScattering.r,
      params.rayleighScattering.g,
      params.rayleighScattering.b,
    );

    // Optical depth per channel, and the transmittance it implies.
    const tauRayleigh = betaRayleigh.mul(rayleighColumn);
    const tauMie = mieColumn.mul(params.mieExtinction);
    const tau = tauRayleigh.add(tauMie);
    const transmittance = exp(tau.negate());

    // --- in-scattering -----------------------------------------------------------------------
    // Phase functions, evaluated on the angle between the view ray and the sun. `toSurface`
    // points away from the eye, so the eye is looking *from* the direction `−viewDirection`.
    const cosTheta = clamp(dot(viewDirection.negate(), vec3(this.sunDirection)), -1, 1);
    const phaseR = rayleighPhaseNode(cosTheta);
    const phaseM = miePhaseNode(cosTheta, params.mieAnisotropy);

    /*
     * The analytic single-scattering result.
     *
     * `L = ∫ σ_s·P(θ)·T_sun·T_view ds`, and for constant single-scatter albedo along the path
     * that integrates to `(τ_s·P/τ_e)·(1 − T)·T_sun`. There is deliberately **no 4π** here: both
     * phase functions are normalised to unit integral over the sphere (asserted in
     * `model.test.ts`), so the source term is `σ_s·P·E_sun` directly. This has to match
     * `skyRadiance` term for term or the haze on a ridge and the sky above it converge on
     * different colours, which is the exact artefact this rewrite exists to remove.
     *
     * Carrying Rayleigh and Mie separately is what makes distant terrain blue while haze looked
     * at through the sun goes white — one expression, both behaviours.
     *
     * Ozone is omitted: its layer is at 25 km and its density below 10 km is a few percent of
     * peak, so it contributes nothing to a path between an aircraft and the ground. The sky
     * model, which integrates to 100 km, does include it.
     */
    const mieAlbedo = params.mieScattering / params.mieExtinction;
    const tauMieScattering = tauMie.mul(mieAlbedo);
    const safeTau = max(tau, vec3(1e-9));

    const scatteringWeighted = tauRayleigh.mul(phaseR).add(tauMieScattering.mul(phaseM));
    const removed = float(1).sub(transmittance);

    const single = scatteringWeighted
      .div(safeTau)
      .mul(removed)
      .mul(vec3(this.sunTransmittance));

    // …plus the light that got here by more than one bounce, which is what keeps shadowed
    // terrain at 40 km hazy grey-blue rather than black. Weighted by the same single-scatter
    // albedo, since absorbed light does not come back however many times it bounces.
    const albedo = tauRayleigh.add(tauMieScattering).div(safeTau);
    const multiple = vec3(this.ambient).mul(albedo).mul(removed);

    const inscatter = single.add(multiple);

    // `scene.fogNode` may be any node; three assigns the lit result to `output` and takes this
    // as the new fragment colour. So, unlike the built-in `fog()` helper — which can only mix
    // toward a single colour — this does the physically correct thing: attenuate what is behind
    // the air per channel, then add what the air itself is emitting toward the eye.
    this.node = vec4(output.rgb.mul(transmittance).add(inscatter), output.a);
  }

  setSunDirection(direction: Vector3): void {
    this.sunDirection.value.copy(direction).normalize();
  }

  /**
   * Extra aerosol, 1 = clean air.
   *
   * Only Mie is scaled: haze is aerosol, and scaling the Rayleigh term instead would change the
   * colour of the sky rather than its clarity — a hazy day is not a bluer day.
   */
  setHaze(multiplier: number): void {
    this.haze.value = Math.max(0.2, multiplier);
  }

  /**
   * Push the altitude-dependent constants.
   *
   * The two table lookups are the link to the sky: whatever the sun's colour is up there, it is
   * the colour lighting the haze down here, by construction.
   */
  updateFrame(cameraAltitudeM: number, sunElevationSin: number): void {
    const altitude = Math.max(0, cameraAltitudeM);
    this.cameraAltitude.value = altitude;

    this.transmittanceTable.sample(altitude, sunElevationSin, scratchSun);
    this.sunTransmittance.value.set(scratchSun.r, scratchSun.g, scratchSun.b);

    this.multipleScatteringTable.sample(altitude, sunElevationSin, scratchAmbient);
    this.ambient.value.set(scratchAmbient.r, scratchAmbient.g, scratchAmbient.b);
  }

  /** The ambient term the last update pushed, so the scene light can match it. */
  readAmbient(out: Rgb): Rgb {
    out.r = this.ambient.value.x;
    out.g = this.ambient.value.y;
    out.b = this.ambient.value.z;
    return out;
  }
}

/** `3/(16π)·(1 + cos²θ)`, as a node. Same function as `model.ts`, evaluated on the GPU. */
function rayleighPhaseNode(cosTheta: Node<'float'>) {
  return cosTheta.mul(cosTheta).add(1).mul(3 / (16 * Math.PI));
}

/** Cornette–Shanks, as a node. Constant folding leaves three multiplies and a `pow`. */
function miePhaseNode(cosTheta: Node<'float'>, g: number) {
  const g2 = g * g;
  const numerator = cosTheta.mul(cosTheta).add(1).mul(3 * (1 - g2));
  const denominator = cosTheta
    .mul(-2 * g)
    .add(1 + g2)
    .max(1e-4)
    .pow(1.5)
    .mul(8 * Math.PI * (2 + g2));
  return numerator.div(denominator);
}

// Keep the CPU implementations reachable from here so the two can be diffed in a test.
export { rayleighPhase, miePhase };
